import type { Logger } from 'stream-chat';

import {
  frameTypeOf,
  redactSensitive,
} from 'src/store/messages/chatDiagnosticText';

const MAX_ENTRIES = 20;
const MESSAGE_MAX_LENGTH = 120;
const URL_PATTERN = /\b(?:wss?|https?):\/\/\S+/gi;

export interface SocketLogEntry {
  /** performance.now() when the SDK logged the line. */
  at: number;
  level: string;
  message: string;
  code?: number;
  reason?: string;
  wasClean?: boolean;
  frameType?: string;
}

const buffer: SocketLogEntry[] = [];

// The SDK logs the websocket URL, whose query string carries the user's chat token, so URLs are
// dropped and only an allowlist of event fields is kept.
const readEvent = (event: unknown) => {
  if (typeof event !== 'object' || event === null) {
    return {};
  }
  const { code, reason, wasClean, data } = event as Record<string, unknown>;
  return {
    code: typeof code === 'number' ? code : undefined,
    reason:
      typeof reason === 'string' && reason
        ? redactSensitive(reason).slice(0, MESSAGE_MAX_LENGTH)
        : undefined,
    wasClean: typeof wasClean === 'boolean' ? wasClean : undefined,
    frameType: frameTypeOf(data),
  };
};

export const chatSocketLogger: Logger = (level, message, extra) => {
  const tags = extra?.tags;
  if (!Array.isArray(tags) || !tags.includes('connection')) {
    return;
  }
  buffer.push({
    at: performance.now(),
    level,
    message: redactSensitive(message.replace(URL_PATTERN, '[url]')).slice(
      0,
      MESSAGE_MAX_LENGTH,
    ),
    ...readEvent(extra?.event),
  });
  if (buffer.length > MAX_ENTRIES) {
    buffer.shift();
  }
};

export const getChatSocketLog = (): SocketLogEntry[] =>
  buffer.map((entry) => ({ ...entry }));

export const resetChatSocketLogForTests = () => {
  buffer.length = 0;
};
